import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState } from "@/ui";
import { useT, FEATURES, type NavItem } from "@/app";
import { useAuthStore } from "@/auth";
import "./dashboard.css";

interface LauncherItem extends NavItem {
  moduleId: string;
}

/**
 * DashboardPage — the post-login landing + role-aware home.
 *
 * It does NOT gate on a feature capability (every authenticated user lands here), but its content is
 * role-aware: it composes the launcher from the FEATURE REGISTRY and shows only the entry-points the
 * current user may actually reach. The gating mirrors the AppShell side-nav exactly — an item is
 * surfaced only when ALL of (module.requiredCapabilities ∪ item.requiredCapabilities) are held — so
 * the home, the nav, and the router agree. The capability Set comes from the auth store (the same
 * source useCan reads); it is server-authoritative and the server re-checks every request.
 */
export function DashboardPage() {
  const { t } = useT();
  const user = useAuthStore((s) => s.user);
  const lab = useAuthStore((s) => s.lab);
  const capabilities = useAuthStore((s) => s.capabilities);
  const capCount = capabilities.size;

  // Compose the capability-permitted launcher from the registry (excluding this dashboard itself),
  // grouped by section and ordered — identical filter to AppShell so home ⇔ nav stay consistent.
  const sections = useMemo(() => {
    const items: LauncherItem[] = [];
    for (const mod of FEATURES) {
      if (mod.id === "dashboard") continue;
      const moduleAllowed = mod.requiredCapabilities.every((c) => capabilities.has(c));
      if (!moduleAllowed) continue;
      for (const item of mod.nav) {
        const itemAllowed = (item.requiredCapabilities ?? []).every((c) => capabilities.has(c));
        if (itemAllowed) items.push({ ...item, moduleId: mod.id });
      }
    }
    items.sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
    const grouped = new Map<string, LauncherItem[]>();
    for (const it of items) {
      const key = it.section ?? "";
      const arr = grouped.get(key) ?? [];
      arr.push(it);
      grouped.set(key, arr);
    }
    return [...grouped.entries()];
  }, [capabilities]);

  const total = sections.reduce((n, [, items]) => n + items.length, 0);

  const renderLabel = (label: ReactNode): ReactNode =>
    typeof label === "string" && label.includes(".") ? t(label) : label;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--nv-s-3)" }}>
        <h1 style={{ fontSize: "var(--nv-fs-h1)", margin: 0 }}>{t("nav.dashboard")}</h1>
        <Badge tone="info">{t("app.signature")}</Badge>
      </header>

      <Card title={t("dashboard.session.title")}>
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "var(--nv-s-2) var(--nv-s-5)",
            margin: 0,
          }}
        >
          <dt style={{ color: "var(--nv-slate)" }}>{t("dashboard.session.user")}</dt>
          <dd className="nv-mono" style={{ margin: 0 }}>
            {user?.email || user?.id || "—"}
          </dd>
          <dt style={{ color: "var(--nv-slate)" }}>{t("dashboard.session.lab")}</dt>
          <dd className="nv-mono" style={{ margin: 0 }}>
            {lab?.nom ?? user?.laboratoireId ?? "—"}
          </dd>
          <dt style={{ color: "var(--nv-slate)" }}>{t("dashboard.session.caps")}</dt>
          <dd className="nv-mono" style={{ margin: 0 }}>
            {capCount}
          </dd>
        </dl>
      </Card>

      <Card title={t("dashboard.launcher.title")}>
        {total === 0 ? (
          <EmptyState
            title={t("dashboard.launcher.empty.title")}
            description={t("dashboard.launcher.empty.desc")}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
            {sections.map(([section, items]) => (
              <div key={section || "_"}>
                {section && (
                  <div
                    style={{
                      fontSize: "var(--nv-fs-label)",
                      color: "var(--nv-slate)",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      marginBottom: "var(--nv-s-2)",
                    }}
                  >
                    {renderLabel(section)}
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: "var(--nv-s-3)",
                  }}
                >
                  {items.map((item) => (
                    <Link
                      key={`${item.moduleId}:${item.id}`}
                      to={item.path}
                      className="nv-launcher-tile"
                    >
                      <span className="nv-launcher-tile__icon" aria-hidden>
                        {item.icon ?? "•"}
                      </span>
                      <span className="nv-launcher-tile__label">{renderLabel(item.label)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
